/**
 * Market Watch Page - Optimized Layout with Compact Design
 */

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addToWatchlist, removeFromWatchlist } from '../redux/slices/watchlistSlice';
import aliceBlueWS from '../services/aliceBlueWebSocket';
import api from '../services/api';

const MarketWatch = () => {
  const dispatch = useDispatch();
  const watchlist = useSelector((state) => state.watchlist.items);
  const { userId } = useSelector((state) => state.auth);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [livePrices, setLivePrices] = useState({});
  const [wsConnected, setWsConnected] = useState(false);
  const [wsStatus, setWsStatus] = useState('Initializing...');
  const [connectionError, setConnectionError] = useState(null);
  const [exchangeFilter, setExchangeFilter] = useState('ALL');
  const [activeTab, setActiveTab] = useState('ALL');
  
  // Order form state
  const [orderFormVisible, setOrderFormVisible] = useState(false);
  const [orderData, setOrderData] = useState({
    symbol: null,
    action: 'BUY',
    quantity: '',
    price: '',
    orderType: 'LIMIT',
    validity: 'DAY',
    product: 'MIS'
  });

  const exchanges = ['ALL', 'NSE', 'NFO', 'BSE', 'BFO', 'MCX', 'CDS'];

  useEffect(() => {
    // Connect to AliceBlue WebSocket on mount
    setWsStatus('Connecting to WebSocket...');
    
    const connectWebSocket = async () => {
      try {
        if (!aliceBlueWS.isConnected) {
          await aliceBlueWS.connect();
          setWsStatus('Connected');
        }
      } catch (err) {
        console.error('Failed to connect to WebSocket:', err);
        setConnectionError(err.message);
        setWsStatus('Connection Failed');
      }
    };

    connectWebSocket();

    // Subscribe to connection status
    const connectionCallback = (connected) => {
      setWsConnected(connected);
      setWsStatus(connected ? 'Connected' : 'Disconnected');
      if (connected) {
        setConnectionError(null);
        if (watchlist.length > 0) {
          // Re-subscribe to watchlist when reconnected
          const symbols = watchlist.map(item => ({
            exchange: item.exchange,
            token: item.instrumentId
          }));
          aliceBlueWS.subscribe(symbols);
        }
      }
    };
    aliceBlueWS.addSubscriber('_connection', connectionCallback);

    // Subscribe to all price updates
    const priceCallback = (data) => {
      setLivePrices(prev => {
        const key = `${data.exchange}|${data.token}`;
        return {
          ...prev,
          [key]: {
            ...prev[key],
            ...data,
            lp: (data.lp && data.lp > 0) ? data.lp : (prev[key]?.lp || data.lp),
            pc: (data.pc !== undefined && data.pc !== null) ? data.pc : (prev[key]?.pc)
          }
        };
      });
    };
    aliceBlueWS.addSubscriber('*', priceCallback);

    return () => {
      aliceBlueWS.removeSubscriber('_connection', connectionCallback);
      aliceBlueWS.removeSubscriber('*', priceCallback);
    };
  }, []);

  useEffect(() => {
    // Subscribe to watchlist symbols when they change
    if (wsConnected && watchlist.length > 0) {
      const symbols = watchlist.map(item => ({
        exchange: item.exchange,
        token: item.instrumentId
      }));
      aliceBlueWS.subscribe(symbols);
    }
  }, [watchlist, wsConnected]);

  const handleSearch = async () => {
    if (searchQuery.length < 2) return;

    try {
      let url = `/symbols/search?q=${searchQuery}&limit=20`;
      if (exchangeFilter !== 'ALL') {
        url += `&exchange=${exchangeFilter}`;
      }
      
      const response = await api.get(url);
      if (response.data.success) {
        setSearchResults(response.data.data);
      }
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  const handleAddToWatchlist = (symbol) => {
    dispatch(addToWatchlist(symbol));
    setSearchResults([]);
    setSearchQuery('');
  };

  const handleRemoveFromWatchlist = (symbol) => {
    dispatch(removeFromWatchlist(symbol));
  };

  const handleBuySell = (symbol, action) => {
    const ltp = getLivePrice(symbol.exchange, symbol.instrumentId);
    setOrderData({
      symbol: symbol,
      action: action,
      quantity: symbol.lotSize || '',
      price: ltp || '',
      orderType: 'LIMIT',
      validity: 'DAY',
      product: 'MIS'
    });
    setOrderFormVisible(true);
    document.getElementById('order-form-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    
    if (!orderData.symbol || !orderData.quantity) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const orderPayload = {
        exchange: orderData.symbol.exchange,
        tradingSymbol: orderData.symbol.tradingSymbol,
        instrumentId: orderData.symbol.instrumentId,
        transactionType: orderData.action,
        quantity: parseInt(orderData.quantity),
        price: orderData.orderType === 'MARKET' ? 0 : parseFloat(orderData.price),
        orderType: orderData.orderType,
        validity: orderData.validity,
        product: orderData.product,
        orderComplexity: 'REGULAR',
        deviceId: '123',
        userId: userId
      };

      const response = await api.post('/orders/place', orderPayload);
      
      if (response.data.success) {
        alert(`Order placed successfully! Order ID: ${response.data.data.brokerOrderId || 'N/A'}`);
        setOrderFormVisible(false);
        setOrderData({
          symbol: null,
          action: 'BUY',
          quantity: '',
          price: '',
          orderType: 'LIMIT',
          validity: 'DAY',
          product: 'MIS'
        });
      } else {
        alert('Order failed: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Order placement error:', error);
      alert('Failed to place order: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCancelOrder = () => {
    setOrderFormVisible(false);
    setOrderData({
      symbol: null,
      action: 'BUY',
      quantity: '',
      price: '',
      orderType: 'LIMIT',
      validity: 'DAY',
      product: 'MIS'
    });
  };

  const getLivePrice = (exchange, token) => {
    const key = `${exchange}|${token}`;
    const price = livePrices[key]?.lp;
    return (price && price > 0) ? price : null;
  };

  const getPriceChange = (exchange, token) => {
    const key = `${exchange}|${token}`;
    const change = livePrices[key]?.pc;
    return (change !== undefined && change !== null) ? change : null;
  };

  // Filter watchlist by active tab
  const getFilteredWatchlist = () => {
    if (activeTab === 'ALL') {
      return watchlist;
    }
    return watchlist.filter(item => item.exchange === activeTab);
  };

  // Get unique exchanges from watchlist
  const getWatchlistExchanges = () => {
    const exchangesInWatchlist = [...new Set(watchlist.map(item => item.exchange))];
    return ['ALL', ...exchangesInWatchlist.sort()];
  };

  const filteredWatchlist = getFilteredWatchlist();
  const watchlistTabs = getWatchlistExchanges();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100">
      <div className="max-w-full mx-auto p-1">
        {/* Compact Header */}
        <div className="flex items-center justify-between py-2 px-2">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
              <span className="text-lg">📊</span>
            </div>
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
                Market Watch
              </h1>
              <p className="text-xs text-gray-600">Real-time trading data</p>
            </div>
          </div>
          
          {/* Connection Status */}
          <div className="flex items-center gap-2">
            <div className={`px-3 py-1 rounded-lg text-xs font-medium flex items-center ${
              wsConnected 
                ? 'bg-green-100 text-green-800' 
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              <span className="mr-1">{wsConnected ? '🟢' : '⚠️'}</span>
              {wsConnected ? 'Live' : 'Connecting'}
            </div>
            {connectionError && (
              <div className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">
                Error
              </div>
            )}
          </div>
        </div>

        {/* Compact Search Section */}
        <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md border border-white/30 p-3 mb-3 mx-1">
          {/* Exchange Filter Buttons */}
          <div className="flex gap-1 mb-3 flex-wrap">
            {exchanges.map(ex => (
              <button
                key={ex}
                onClick={() => setExchangeFilter(ex)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                  exchangeFilter === ex
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {ex === 'ALL' ? 'ALL' : ex}
              </button>
            ))}
          </div>

          <div className="flex space-x-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search symbols (e.g., RELIANCE, NIFTY, BANKNIFTY)"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white placeholder-gray-500 text-sm"
            />
            <button 
              onClick={handleSearch} 
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Search
            </button>
          </div>

          {/* Compact Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-medium text-gray-600 mb-2">Results ({searchResults.length})</div>
              <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                {searchResults.map((symbol, idx) => (
                  <div key={idx} className="flex justify-between items-center p-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 text-sm">{symbol.tradingSymbol}</p>
                      <p className="text-xs text-gray-600">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${
                          symbol.exchange === 'NSE' ? 'bg-blue-500' :
                          symbol.exchange === 'NFO' ? 'bg-purple-500' :
                          symbol.exchange === 'BSE' ? 'bg-green-500' :
                          symbol.exchange === 'BFO' ? 'bg-orange-500' :
                          symbol.exchange === 'MCX' ? 'bg-yellow-500' : 'bg-indigo-500'
                        }`}></span>
                        {symbol.exchange}
                      </p>
                    </div>
                    <button
                      onClick={() => handleAddToWatchlist(symbol)}
                      className="bg-green-600 hover:bg-green-700 text-white text-xs font-medium py-1 px-3 rounded transition-colors"
                    >
                      Add
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Compact Watchlist */}
        <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md border border-white/30 p-3 mx-1">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <div className="w-6 h-6 bg-purple-600 rounded-lg flex items-center justify-center mr-2">
                <span className="text-sm text-white">👁️</span>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-800">Watchlist</h2>
                <p className="text-xs text-gray-600">
                  {filteredWatchlist.length} {activeTab === 'ALL' ? 'total' : activeTab} symbols
                </p>
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg p-2 border border-blue-200">
              <div className="text-center">
                <div className="text-sm font-bold text-blue-600">
                  {filteredWatchlist.length}
                </div>
                <div className="text-xs text-gray-500">Symbols</div>
              </div>
            </div>
          </div>

          {/* Compact Order Form */}
          {orderFormVisible && orderData.symbol && (
            <div id="order-form-section" className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center">
                  <div className={`w-6 h-6 rounded flex items-center justify-center mr-2 ${
                    orderData.action === 'BUY' 
                      ? 'bg-green-600' 
                      : 'bg-red-600'
                  }`}>
                    <span className="text-xs text-white">{orderData.action === 'BUY' ? '↗' : '↘'}</span>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-800">
                      {orderData.action} {orderData.symbol.tradingSymbol}
                    </h3>
                    <p className="text-xs text-gray-600">{orderData.symbol.exchange}</p>
                  </div>
                </div>
                <button
                  onClick={handleCancelOrder}
                  className="w-6 h-6 bg-gray-200 hover:bg-gray-300 rounded flex items-center justify-center text-xs"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleOrderSubmit} className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                {/* Action Buttons */}
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Action</label>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => setOrderData({...orderData, action: 'BUY'})}
                      className={`flex-1 py-2 px-3 rounded text-sm font-semibold ${
                        orderData.action === 'BUY'
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      BUY
                    </button>
                    <button
                      type="button"
                      onClick={() => setOrderData({...orderData, action: 'SELL'})}
                      className={`flex-1 py-2 px-3 rounded text-sm font-semibold ${
                        orderData.action === 'SELL'
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      SELL
                    </button>
                  </div>
                </div>

                {/* Quantity */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Qty {orderData.symbol.lotSize && `(${orderData.symbol.lotSize})`}
                  </label>
                  <input
                    type="number"
                    value={orderData.quantity}
                    onChange={(e) => setOrderData({...orderData, quantity: e.target.value})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Qty"
                    required
                  />
                </div>

                {/* Price */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Price
                  </label>
                  <input
                    type="number"
                    step="0.05"
                    value={orderData.price}
                    onChange={(e) => setOrderData({...orderData, price: e.target.value})}
                    className={`w-full px-2 py-1 border rounded text-sm ${
                      orderData.orderType === 'MARKET' 
                        ? 'border-gray-200 bg-gray-100 cursor-not-allowed'
                        : 'border-gray-300 focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    placeholder="Price"
                    disabled={orderData.orderType === 'MARKET'}
                    required={orderData.orderType !== 'MARKET'}
                  />
                </div>

                {/* Order Type */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={orderData.orderType}
                    onChange={(e) => setOrderData({...orderData, orderType: e.target.value})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="LIMIT">LIMIT</option>
                    <option value="MARKET">MARKET</option>
                    <option value="SL">SL</option>
                    <option value="SLM">SLM</option>
                  </select>
                </div>

                {/* Product */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Product</label>
                  <select
                    value={orderData.product}
                    onChange={(e) => setOrderData({...orderData, product: e.target.value})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="MIS">MIS</option>
                    <option value="NRML">NRML</option>
                    <option value="CNC">CNC</option>
                  </select>
                </div>

                {/* Validity */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Validity</label>
                  <select
                    value={orderData.validity}
                    onChange={(e) => setOrderData({...orderData, validity: e.target.value})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="DAY">DAY</option>
                    <option value="IOC">IOC</option>
                  </select>
                </div>

                {/* Submit Button */}
                <div className="col-span-2 md:col-span-1 flex items-end">
                  <button
                    type="submit"
                    className={`w-full py-2 px-3 rounded text-sm font-semibold text-white transition-colors ${
                      orderData.action === 'BUY' 
                        ? 'bg-green-600 hover:bg-green-700' 
                        : 'bg-red-600 hover:bg-red-700'
                    }`}
                  >
                    Place Order
                  </button>
                </div>
              </form>

              {/* Compact Symbol Info */}
              <div className="bg-white p-2 rounded border text-xs text-gray-600">
                <strong>Symbol:</strong> {orderData.symbol.tradingSymbol} | 
                <strong>Exchange:</strong> {orderData.symbol.exchange} | 
                <strong>LTP:</strong> ₹{getLivePrice(orderData.symbol.exchange, orderData.symbol.instrumentId)?.toFixed(2) || '—'}
              </div>
            </div>
          )}

          {/* Compact Exchange Tabs */}
          {watchlist.length > 0 && (
            <div className="mb-3">
              <div className="flex gap-1 mb-3 flex-wrap">
                {watchlistTabs.map(exchange => {
                  const count = exchange === 'ALL' 
                    ? watchlist.length 
                    : watchlist.filter(item => item.exchange === exchange).length;
                  
                  return (
                    <button
                      key={exchange}
                      onClick={() => setActiveTab(exchange)}
                      className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                        activeTab === exchange
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {exchange} ({count})
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {filteredWatchlist.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="table-auto w-full bg-white">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Symbol</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Exchange</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">LTP</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Change</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredWatchlist.map((item, idx) => {
                    const ltp = getLivePrice(item.exchange, item.instrumentId);
                    const change = getPriceChange(item.exchange, item.instrumentId);
                    
                    return (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <div className="text-sm font-semibold text-gray-900">{item.tradingSymbol}</div>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                            item.exchange === 'NSE' ? 'bg-blue-100 text-blue-800' :
                            item.exchange === 'NFO' ? 'bg-purple-100 text-purple-800' :
                            item.exchange === 'BSE' ? 'bg-green-100 text-green-800' :
                            item.exchange === 'BFO' ? 'bg-orange-100 text-orange-800' :
                            item.exchange === 'MCX' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {item.exchange}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <div className={`text-sm font-semibold ${
                            ltp !== null ? 'text-blue-600' : 'text-gray-400'
                          }`}>
                            {ltp !== null ? `₹${ltp.toFixed(2)}` : '—'}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                            change !== null && parseFloat(change) >= 0 
                              ? 'text-green-700 bg-green-100' 
                              : change !== null 
                                ? 'text-red-700 bg-red-100' 
                                : 'text-gray-400 bg-gray-100'
                          }`}>
                            {change !== null ? (
                              `${parseFloat(change) > 0 ? '+' : ''}${parseFloat(change).toFixed(2)}%`
                            ) : '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex space-x-1">
                            <button
                              onClick={() => handleBuySell(item, 'BUY')}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs font-medium py-1 px-2 rounded transition-colors"
                            >
                              Buy
                            </button>
                            <button
                              onClick={() => handleBuySell(item, 'SELL')}
                              className="bg-red-600 hover:bg-red-700 text-white text-xs font-medium py-1 px-2 rounded transition-colors"
                            >
                              Sell
                            </button>
                            <button
                              onClick={() => handleRemoveFromWatchlist(item)}
                              className="bg-gray-500 hover:bg-gray-600 text-white text-xs font-medium py-1 px-2 rounded transition-colors"
                            >
                              ✕
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-lg">📊</span>
              </div>
              {watchlist.length === 0 ? (
                <div>
                  <h3 className="text-base font-bold text-gray-700 mb-2">
                    No Symbols in Watchlist
                  </h3>
                  <p className="text-sm text-gray-500 mb-3">
                    Search and add symbols to monitor live data
                  </p>
                  <div className="text-xs text-gray-400">
                    Try: RELIANCE, NIFTY, BANKNIFTY
                  </div>
                </div>
              ) : (
                <div>
                  <h3 className="text-base font-bold text-gray-700 mb-2">
                    No {activeTab} Symbols
                  </h3>
                  <button
                    onClick={() => setActiveTab('ALL')}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm py-1 px-3 rounded transition-colors"
                  >
                    View All
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketWatch;
